import TabList from '@mui/lab/TabList';
import {
	Tabs,
	styled,
	Tab,
	Box,
	Stack,
	TabScrollButton,
	TabsProps,
	TabProps,
} from '@mui/material';

import * as React from 'react';
import { useEffect } from 'react';

import { ITabValues } from 'common/api/type-def';
import { toSentenceCase } from 'common/utils/utilFunctions';

import styles from './TabStyle.module.scss';

const {
	lightBackgroundColor,
	tabsBackground,
	textColor,
	textDarkColor,
	selectedTabText,
	selectedTabBackground,
} = styles;

interface StyledTabsProps extends TabsProps {
	greyBg?: boolean;
	children?: React.ReactNode;
	value: number;
	onChange: (event: React.SyntheticEvent, newValue: number) => void;
}

export const StyledTabs = styled((props: StyledTabsProps) => (
	<Tabs
		{...props}
		TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
	/>
))((props: StyledTabsProps) => ({
	paddingTop: '6px',
	backgroundColor: props.greyBg ? lightBackgroundColor : tabsBackground,
	minHeight: '43px',
	width: 'fit-content',
	paddingLeft: '6px',
	borderRadius: '4px',
	marginTop: '36px',
	'@media (max-width: 1024px)': {
		width: 'min-content',
		height: 'fit-content',
		paddingLeft: '3px',
	},
	'& .MuiTabs-indicator': {
		display: 'flex',
		justifyContent: 'center',
		backgroundColor: 'transparent',
	},
}));

const StyledTabScrollButton = styled(TabScrollButton)({
	'&.Mui-disabled': {
		width: 0,
	},
	overflow: 'hidden',
});
export const StyledTabsList = styled((props: any) => (
	<TabList
		{...props}
		variant="scrollable"
		ScrollButtonComponent={StyledTabScrollButton}
		scrollButtons
		aria-label="scrollable force tabs example"
		TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
	/>
))({
	paddingTop: '6px',
	paddingBottom: '6px',
	backgroundColor: tabsBackground,
	width: 'fit-content',
	paddingLeft: '6px',
	borderRadius: '4px',
	alignItems: 'center',
	gap: '8px',
	minHeight: '1rem',
	padding: '0px 10px',
	fontSize: '16px',
	color: textColor,
	'@media (max-width: 1024px)': {
		width: 'min-content',
		height: 'fit-content',
		paddingLeft: '3px',
	},
	'& .MuiTabs-indicator': {
		display: 'flex',
		justifyContent: 'center',
		backgroundColor: 'transparent',
	},

	'& .MuiTabs-scrollButtons.Mui-disabled': {
		opacity: '0.3',
		display: 'none',
	},
	'& .MuiButtonBase-root.MuiTab-root.Mui-selected': {
		color: selectedTabText,
		background: selectedTabBackground,
		height: '1rem',
		alignItems: 'center',
		gap: '8px',
		backgroundColor: selectedTabBackground,
		borderRadius: '5px',
		minHeight: '2.5rem',
		marginTop: '4px',
	},
	'&.Mui-selected': {
		color: selectedTabText,

		'@media (max-width: 1024px)': {
			width: 'min-content',
			height: 'fit-content',
			gap: '5px',
		},
	},
});
interface StyledTabProps extends TabProps {
	greyBg?: boolean;
	disabled?: boolean;
}

export const StyledTab = styled((props: StyledTabProps) => (
	<Tab disabled={props?.disabled} disableRipple {...props} />
))(({ theme, greyBg = false }) => ({
	textTransform: 'none',
	fontWeight: theme.typography.fontWeightRegular,
	marginRight: theme.spacing(1),
	minHeight: '1.5rem',
	fontSize: '16px',
	color: greyBg ? textDarkColor : textColor,
	padding: '0px 10px',
	'@media (max-width: 1024px)': {
		width: 'min-content',
		height: 'fit-content',
		padding: '0px 6px',
	},
	'&.Mui-selected': {
		color: greyBg ? selectedTabBackground : selectedTabText,
		height: greyBg ? '48px' : '32px',
		alignItems: 'center',
		gap: '8px',
		fill: greyBg ? lightBackgroundColor : selectedTabBackground,
		backgroundColor: greyBg ? lightBackgroundColor : selectedTabBackground,
		borderRadius: '2px',
		minHeight: '1.5rem',
		'@media (max-width: 1024px)': {
			width: 'min-content',
			height: 'fit-content',
			gap: '5px',
		},
	},
	'&.Mui-focusVisible': {
		backgroundColor: selectedTabBackground,
	},
}));

export const TabPanel = (props: TabPanelProps) => {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`full-width-tabpanel-${index}`}
			aria-labelledby={`full-width-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box>
					<div>{children}</div>
				</Box>
			)}
		</div>
	);
};

interface TabPanelProps {
	children: React.ReactNode;
	index: number;
	value: number;
}

export interface ITabPanel {
	id: number;
	key?: string;
	children: React.JSX.Element;
	hideSentenceCase?: boolean;
}

interface IProperty {
	greyBg?: boolean;
	details: ITabValues[];
	components: ITabPanel[];
	showCount: boolean;
	showButton: boolean;
	childComponent?: React.ReactNode;
	onTabChange?: (newValue: number) => void;
	testId?: string;
	activeTab?: number;
}

const TabBar = (props: IProperty) => {
	const { details, greyBg, childComponent, testId = 'tabs-sample' } = props;
	const { components, showCount, onTabChange, activeTab } = props;

	const [value, setValue] = React.useState(activeTab || 0);

	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
		if (onTabChange) {
			onTabChange(newValue);
		}
	};

	const handleLabel = (res: any) => {
		const displayName = res?.hideSentenceCase
			? res.name
			: toSentenceCase(res.name);
		return displayName;
	};

	useEffect(() => {
		if (activeTab) {
			setValue(activeTab);
		}
	}, [activeTab]);
	return (
		<div className={styles['riskreference-tab']}>
			<div className={styles['changes-for-your-attention-tabs-block']}>
				{childComponent ? (
					<Stack direction="row" className={styles['stack-display']}>
						<StyledTabs
							greyBg={greyBg}
							value={value || 0}
							onChange={handleChange}
							aria-label="scrollable prevent tabs example"
						>
							{details.map((res) => (
								<StyledTab
									disabled={res?.disabled}
									key={`${res.name}-${testId}`}
									data-testId={testId}
									label={
										showCount
											? `${handleLabel(res)} (${res.count})`
											: handleLabel(res)
									}
								/>
							))}
						</StyledTabs>

						{childComponent}
					</Stack>
				) : (
					<StyledTabs
						greyBg={greyBg}
						value={value || 0}
						onChange={handleChange}
						aria-label="scrollable prevent tabs example"
					>
						{details.map((res) => (
							<StyledTab
								disabled={res?.disabled}
								key={`${res.name}-${testId}`}
								data-testId={testId}
								label={
									showCount
										? `${handleLabel(res)} (${res.count})`
										: handleLabel(res)
								}
							/>
						))}
					</StyledTabs>
				)}
				{components.map((res, i) => (
					<TabPanel
						value={value || 0}
						index={i}
						key={`${res.id}-tab-panel-${res.key}`}
					>
						{res.children}
					</TabPanel>
				))}
			</div>
		</div>
	);
};

export default TabBar;
